import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import queryString from "query-string"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { Tag } from "@/api/tags"
import type { Interval } from "@/metrics"
import type { IrohDeployInfo, ProjectIdParam } from "@/types"
import { formatDateWithoutMillsecondsAndZ } from "@/util"

type RelayTimeseriesParams = {
  relays?: string[] // list of relay names to filter by
  from: Date // EARLIEST point in time
  to: Date // LATEST point in time
  interval: Interval // interval of time between each data point
}

function relayTimeseriesQuery(p: RelayTimeseriesParams) {
  return queryString.stringify(
    {
      from: formatDateWithoutMillsecondsAndZ(p.from),
      to: formatDateWithoutMillsecondsAndZ(p.to),
      bucket: p.interval,
      relays: p.relays,
    },
    {
      arrayFormat: "bracket",
    },
  )
}

type RelaySample = {
  timestamp: number
  value: number
}

type MachineHealthSample = {
  metric: {
    instance: string
  }
  sample: {
    timestamp: number
    value: number
  }
}

type MachineHealthSamples = Record<string, MachineHealthSample[]>

export type MachineStats = {
  cpu: number
  mem: number
  rxByInstance: number
  txByInstance: number
  rxTotal: number
  txTotal: number
  rx: number
  tx: number
}

// Each series is optional: the backend fans out four Prometheus queries in
// parallel and returns whichever ones succeed. A single failing series no
// longer fails the whole request — consumers MUST handle missing keys as
// "no data" (e.g. via `data?.Tx?.[0] ?? []`) rather than asserting presence.
export type HostSeriesStats = {
  Rx?: RelaySample[][]
  Tx?: RelaySample[][]
  RxByInstance?: RelaySample[][]
  TxByInstance?: RelaySample[][]
}

type PrometheusResponse = { sample: { timestamp: number; value: number } }[]
type PrometheusTotal = { timestamp: number; value: number }[][]

// Define a service using a base URL and expected endpoints
export const relaysApi = createApi({
  reducerPath: "relays",
  tagTypes: [Tag.Relays],
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}relays`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    relaysGet: build.query<IrohDeployInfo[], ProjectIdParam>({
      query: (params) => ({
        url: `${params.project_id}`,
      }),
      providesTags: (_result, _error, params) => [
        { type: Tag.Relays, id: params.project_id },
      ],
    }),
    relayConnectionsGet: build.query<
      RelaySample[],
      ProjectIdParam & RelayTimeseriesParams
    >({
      query: (params) => ({
        url: `${params.project_id}/connections?${relayTimeseriesQuery(params)}`,
      }),
      transformResponse: (
        baseQueryReturnValue: PrometheusTotal,
        _meta,
        _arg,
      ) => {
        return baseQueryReturnValue[0]
      },
    }),
    relayLiveConnectionsGet: build.query<
      number | undefined,
      ProjectIdParam & { relays?: string[] }
    >({
      query: ({ project_id, relays }) => ({
        url: `${project_id}/live_connections?${queryString.stringify(
          { relays },
          {
            arrayFormat: "bracket",
          },
        )}`,
      }),
      transformResponse: (
        baseQueryReturnValue: PrometheusResponse,
        _meta,
        _arg,
      ) => {
        if (baseQueryReturnValue.length === 0) {
          return undefined
        }
        return baseQueryReturnValue[0].sample.value
      },
    }),

    // Returns time-series data for the given hosts
    relayGetHostSeries: build.query<
      HostSeriesStats,
      ProjectIdParam & RelayTimeseriesParams
    >({
      query: (params) => ({
        url: `${params.project_id}/host-series?${relayTimeseriesQuery(params)}`,
      }),
    }),

    // Returns the last sample for the given hosts
    relayGetHostStats: build.query<
      Record<string, MachineStats>,
      ProjectIdParam & RelayTimeseriesParams
    >({
      query: (params) => ({
        url: `${params.project_id}/host-stats?${relayTimeseriesQuery(params)}`,
      }),
      transformResponse: (
        baseQueryReturnValue: MachineHealthSamples,
        _meta,
        _arg,
      ) =>
        Object.keys(baseQueryReturnValue).reduce(
          (acc, stat) => {
            for (const value of baseQueryReturnValue[stat]) {
              if (!acc[value.metric.instance]) {
                acc[value.metric.instance] = {
                  cpu: -1,
                  mem: -1,
                  rxByInstance: -1,
                  txByInstance: -1,
                  rxTotal: -1,
                  txTotal: -1,
                  rx: -1,
                  tx: -1,
                }
              }
              // lowercase first letter of stat name
              const statKey = stat.charAt(0).toLowerCase() + stat.slice(1)
              acc[value.metric.instance][statKey as keyof MachineStats] =
                value.sample.value
            }
            return acc
          },
          {} as Record<string, MachineStats>,
        ),
    }),
  }),
})

export const {
  useRelaysGetQuery,
  useRelayConnectionsGetQuery,
  useRelayLiveConnectionsGetQuery,
  useRelayGetHostStatsQuery,
  useRelayGetHostSeriesQuery,
} = relaysApi
